import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import { Link, useNavigate, useParams } from "react-router-dom";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import Select from "react-select";
import DropDownStyles, {
  DropDownStylesDark,
  defaultSelectPortalProps,
} from "../staff/dropdownStyles";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import useThemeColor from "../../hooks/useThemeColor";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import swal from "sweetalert";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import { ChoiceButtonGrid } from "../../components/CustomComponents/Buttons/ChoiceButton/choiceButton";
import StatusBadge from "../../components/CustomComponents/StatusBadge/StatusBadge";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../utils/catalogTotalRecords";

import ModernGridToolbar, {
  ModernGridToolbarFilterPill,
} from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import { getModernGridFilterPillSelectStyles } from "../../components/CustomComponents/ModernGridToolbar/modernGridToolbarSelectStyles";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import { resolveMediaUrl } from "../../utils/marketingTemplateUtils";
import "./manageItems.scss";

const ITEM_GRID_COLUMN_ORDER = [
  "image",
  "name",
  "costPrice",
  "SalePrice",
  "deliveryPrice",
  "takeAwayPrice",
  "status",
];

const normalizeItemImageSrc = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return resolveMediaUrl(path);
};

const ItemGridThumbnail = ({ imagePath, alt, isDarkMode }) => {
  const [imageError, setImageError] = useState(false);
  const imageSrc = normalizeItemImageSrc(imagePath);

  return (
    <div
      className={`manage-items-grid__thumb${isDarkMode ? " manage-items-grid__thumb--dark" : ""}`}
    >
      {imageSrc && !imageError ? (
        <img
          src={imageSrc}
          alt={alt || "Product"}
          className="manage-items-grid__thumb-image"
          onError={() => setImageError(true)}
        />
      ) : (
        <i className="fa fa-utensils manage-items-grid__thumb-placeholder" aria-hidden="true" />
      )}
    </div>
  );
};

const ITEM_SORT_FIELDS = {
  name: "MenuItem_Name",
  costPrice: "MenuItem_CostPrice",
  SalePrice: "MenuItem_SalePrice",
  deliveryPrice: "MenuItem_DeliveryPrice",
  takeAwayPrice: "MenuItem_TakeAwayPrice",
  status: "MenuItem_Publish",
  categoryOrder: "CategoryItem_SortOrder",
  globalOrder: "MenuItem_Orderby",
};

const CATEGORY_SORT_BY = { value: "CategoryItem_SortOrder", label: "Category Order" };

const ManageItems = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { RootCat_ID } = useParams();
  const dispatch = useDispatch();

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const filterPillSelectStyles = useMemo(
    () => getModernGridFilterPillSelectStyles(dropdownStyles, isDarkMode),
    [dropdownStyles, isDarkMode]
  );

  const categoryFilterSelectStyles = useMemo(() => {
    const base = filterPillSelectStyles;
    return {
      ...base,
      menu: (provided) => {
        const merged = base.menu ? base.menu(provided) : provided;
        return { ...merged, minWidth: 260 };
      },
    };
  }, [filterPillSelectStyles]);

  const formatCategoryOptionLabel = useCallback((option, { context }) => {
    if (context === "value") {
      return option.label;
    }
    const level = option.level ?? 0;
    return (
      <span
        className="manage-items-category-option"
        style={{ paddingLeft: level * 20 }}
      >
        {option.label}
      </span>
    );
  }, []);

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  



  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [initialItemsLoadComplete, setInitialItemsLoadComplete] = useState(false);
  /** Total items with no search/status/category filters — used for empty-catalog UI only */
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProductType, setSelectedProductType] = useState("Food");
  const [isSecondModalOpen, setIsSecondModalOpen] = useState(false);
  const [selectedSecondOption, setSelectedSecondOption] =
    useState("Add Single Item");
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [compCurrency, setCompCurrency] = useState("GBP");
  const lastSelectedIndex = useRef(null);
  const itemsFetchSeqRef = useRef(0);
  const itemsFetchAbortRef = useRef(null);
  const [categoryOptions, setCategoryOptions] = useState([
    { value: "", label: "All Categories" },
  ]);
  const [statusConfirmItem, setStatusConfirmItem] = useState(null);

  const pageKey = "items";
  const defaultVisibleColumns = {
    image: true,
    name: true,
    SalePrice: true,
    costPrice: false,
    deliveryPrice: false,
    takeAwayPrice: false,
    status: true,
  };

  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "MenuItem_Orderby", label: "Order By" };

  // Filter options
  const defaultStatus = { value: "", label: "All Status" };
  const defaultCategory = { value: "", label: "All Categories" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;

  // Filter states
  const status = preferences.status || defaultStatus;
  const category = preferences.category || defaultCategory;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  const statusSelectValue =
    statusOptions.find((o) => String(o.value) === String(status?.value ?? "")) ??
    defaultStatus;
  const categorySelectValue =
    categoryOptions.find((o) => String(o.value) === String(category?.value ?? "")) ??
    defaultCategory;

  const secondButtons = [
    {
      id: "addSingleItem",
      title: "Add Single Item",
      description: "Best for Restaurants",
      default: true,
    },
    {
      id: "createBulkItems",
      title: "Create Bulk Items",
      description: "Add multiple items at once",
      default: false,
    },
  ];

  // Currency symbol mapping
  const currencySymbols = {
    GBP: "Â£",
    USD: "$",
    EUR: "â‚¬",
    // Add more currency symbols as needed
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const navigate = useNavigate();
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const activeRootCatId = useMemo(
    () => Number(category?.value || RootCat_ID || 0),
    [category?.value, RootCat_ID]
  );
  const isCategorySortMode = activeRootCatId > 0;
  const canDragReorder = isCategorySortMode
    ? sortBy?.value === ITEM_SORT_FIELDS.categoryOrder
    : sortBy?.value === ITEM_SORT_FIELDS.globalOrder;

  // Fetch CompCurrency
  const fetchCompCurrency = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_PROFILE);
      const profileData = response.data.data;
      setCompCurrency(profileData?.compCurrency || "GBP");
    } catch (err) {
      showErrorToast("Failed to load company currency");
    }
  }, []);

  // Fetch Categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/api/RootCategories/BO_GetAllCategories", {
        params: {
          CompID,
          SortBy: 'rootCat_Orderby',
          SortOrder: 'ASC',
          Search: '',
        }
      });

      if (response.data.status && response.data.data) {
        const categories = transformCategoriesToOptions(response.data.data);
        setCategoryOptions([{ value: "", label: "All Categories" }, ...categories]);
      }
    } catch (err) {
      showErrorToast("Failed to load categories");
    }
  }, [CompID]);

  const transformCategoriesToOptions = (categories, level = 0) => {
    const options = [];
    if (!Array.isArray(categories)) return options;

    categories.forEach((category) => {
      options.push({
        value: category.rootCat_ID,
        label: category.rootCat_Name,
        level,
      });

      if (category.subCategoriesList?.length > 0) {
        options.push(
          ...transformCategoriesToOptions(category.subCategoriesList, level + 1)
        );
      }
    });

    return options;
  };

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }

    // Initialize filter states
    if (!preferences.status) {
      dispatch(setFilterField({ pageKey, field: "status", value: defaultStatus }));
    }
    if (!preferences.category) {
      dispatch(setFilterField({ pageKey, field: "category", value: defaultCategory }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }

    // Fetch CompCurrency and Categories on component mount
    fetchCompCurrency();
    fetchCategories();
  }, [dispatch, pageKey, fetchCompCurrency, fetchCategories]);

  useEffect(() => {
    if (!RootCat_ID || categoryOptions.length <= 1) return;
    const match = categoryOptions.find(
      (option) => String(option.value) === String(RootCat_ID)
    );
    if (match && String(category?.value) !== String(RootCat_ID)) {
      dispatch(setFilterField({ pageKey, field: "category", value: match }));
    }
  }, [RootCat_ID, categoryOptions, category?.value, dispatch, pageKey]);

  useEffect(() => {
    if (isCategorySortMode && sortBy?.value === ITEM_SORT_FIELDS.globalOrder) {
      dispatch(setSortBy({ pageKey, sortBy: CATEGORY_SORT_BY }));
      dispatch(setSortOrder({ pageKey, sortOrder: { value: "ASC", label: "ASC" } }));
      return;
    }

    if (!isCategorySortMode && sortBy?.value === ITEM_SORT_FIELDS.categoryOrder) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
  }, [isCategorySortMode, sortBy?.value, dispatch, pageKey]);

  const fetchItemsData = useCallback(async () => {
    const fetchSeq = ++itemsFetchSeqRef.current;

    if (itemsFetchAbortRef.current) {
      itemsFetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    itemsFetchAbortRef.current = controller;

    if (!CompID) {
      if (fetchSeq === itemsFetchSeqRef.current) {
        setItems([]);
        setTotalPages(0);
        setTotalRecords(0);
        setLoading(false);
        setInitialItemsLoadComplete(true);
      }
      return;
    }

    try {
      setLoading(true);
      const rootCatId = Number(category?.value || RootCat_ID || 0) || 0;
      // Avoid invalid SortBy for the "all categories" SP path (no CategoryItem_SortOrder column).
      let effectiveSortBy = sortBy?.value || ITEM_SORT_FIELDS.globalOrder;
      if (!rootCatId && effectiveSortBy === ITEM_SORT_FIELDS.categoryOrder) {
        effectiveSortBy = ITEM_SORT_FIELDS.globalOrder;
      } else if (rootCatId && effectiveSortBy === ITEM_SORT_FIELDS.globalOrder) {
        effectiveSortBy = ITEM_SORT_FIELDS.categoryOrder;
      }

      const response = await axiosInstance.get(APIs.GET_ALL_ITEMS_FOR_GRID, {
        params: {
          CompID,
          SortBy: effectiveSortBy,
          SortOrder: sortOrder?.value || "DESC",
          Search: search?.trim() || "",
          PageNumber: pageNumber,
          PageSize: pageSize?.value || 50,
          RootCat_ID: rootCatId,
          Status: status?.value !== "" ? String(status?.value) : "",
        },
        signal: controller.signal,
      });

      if (controller.signal.aborted || fetchSeq !== itemsFetchSeqRef.current) return;

      if (!response.data?.status) {
        setItems([]);
        setTotalPages(0);
        setTotalRecords(0);
        showErrorToast(response.data?.message || "Failed to load items");
        return;
      }

      const itemsData = Array.isArray(response.data.data) ? response.data.data : [];
      const records = Number(response.data.totalRecords) || 0;

      setItems(itemsData);
      setTotalPages(Number(response.data.totalPages) || 0);
      setTotalRecords(records);

      const noSearch = !search.trim();
      const noStatus = status?.value === "" || status?.value === undefined;
      const noCategory = !category?.value;
      if (noSearch && noStatus && noCategory) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, records);
      }
    } catch (err) {
      if (controller.signal.aborted || err?.code === "ERR_CANCELED" || fetchSeq !== itemsFetchSeqRef.current) {
        return;
      }
      setItems([]);
      setTotalPages(0);
      setTotalRecords(0);
      showErrorToast(err?.response?.data?.message || "Failed to load items");
    } finally {
      if (fetchSeq === itemsFetchSeqRef.current) {
        setLoading(false);
        setInitialItemsLoadComplete(true);
      }
    }
  }, [search, sortBy, sortOrder, CompID, pageNumber, pageSize, status, category, RootCat_ID]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    status?.value,
    category?.value,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
    RootCat_ID,
  ]);

  useInvalidateFetchOnFilterChange(itemsFetchSeqRef, [
    search,
    status?.value,
    category?.value,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
    RootCat_ID,
  ]);

  // Shorter debounce for search typing; other grid pages keep the shared default.
  useDebouncedEffect(() => fetchItemsData(), [fetchItemsData], 400);

  useEffect(() => {
    if (!initialItemsLoadComplete) return;
    setLoading(true);
  }, [
    search,
    status?.value,
    category?.value,
    pageNumber,
    pageSize?.value,
    sortBy?.value,
    sortOrder?.value,
    RootCat_ID,
  ]);

  const handleAddItemClick = () => {
    setIsModalOpen(true);
  };

  const handleProductTypeClick = (buttonName) => {
    setSelectedProductType(buttonName);
    setIsModalOpen(false);
    setIsSecondModalOpen(true);
  };

  const handleSecondClick = (buttonName) => {
    setSelectedSecondOption(buttonName);
    if (buttonName === "Add Single Item") {
      navigate(`/items/createItem/${selectedProductType}`);
      setIsSecondModalOpen(false);
    }
    // Create Bulk Items: no action, modal remains open
  };

  const handleDeleteItem = async (item) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${item.menuItem_Name}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await axiosInstance
        .delete(APIs.DELETE_ITEM, {
          params: {
            CompID,
            MenuItem_ID: item.menuItem_ID,
          },
        })
        .then((response) => {
          if (response.data.status) {
            setItems((prevItems) =>
              prevItems.filter((i) => i.menuItem_ID !== item.menuItem_ID)
            );
            showSuccessToast("Item is Deleted.");
          } else {
            showErrorToast(response.data.message || "Deletion failed");
          }
        });
      setLoading(false);
    } catch (err) {
      showErrorToast("Something Went Wrong.");
      setLoading(false);
    }
  };

  const handleSelectItem = (itemId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);

        for (let i = start; i <= end; i++) {
          if (items[i]?.menuItem_ID) {
            newSelected.add(items[i].menuItem_ID);
          }
        }
      } else {
        if (newSelected.has(itemId)) {
          newSelected.delete(itemId);
        } else {
          newSelected.add(itemId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(items.map((item) => item.menuItem_ID));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No items selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected items?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((itemId) =>
          axiosInstance.delete(APIs.DELETE_ITEM, {
            params: {
              CompID,
              MenuItem_ID: itemId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchItemsData();
      showSuccessToast(`${selectedItems.size} items deleted successfully.`);
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to delete selected items.");
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No items selected for editing.");
      return;
    }

    const selectedItemIds = Array.from(selectedItems);
    navigate(`/items/bulkEditItems`, { state: { selectedItemIds } });
  };

  const handleBulkStatusChange = async (publishStatus) => {
    if (selectedItems.size === 0) {
      showErrorToast("No items selected for status change.");
      return;
    }

    try {
      const confirmStatusChange = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to ${publishStatus ? "publish" : "unpublish"} ${selectedItems.size} selected items?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmStatusChange) return;

      setLoading(true);
      const updatedItems = items.map((item) =>
        selectedItems.has(item.menuItem_ID)
          ? { ...item, menuItem_Publish: publishStatus }
          : item
      );
      setItems(updatedItems);

      await Promise.all(
        Array.from(selectedItems).map((itemId) =>
          axiosInstance.post(APIs.POST_UPDATE_ITEM_STATUS, {
            CompID,
            MenuItem_ID: itemId,
            MenuItem_Publish: publishStatus,
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      showSuccessToast(
        `${selectedItems.size} items status updated successfully.`
      );
      setLoading(false);
    } catch (error) {
      showErrorToast("Failed to update selected items status.");
      fetchItemsData();
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    dispatch(setFilterField({ pageKey, field: "status", value: defaultStatus }));
    dispatch(setFilterField({ pageKey, field: "category", value: defaultCategory }));
  };

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(setSortBy({ pageKey, sortBy: { value: sortField, label: sortField } }));
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const renderBulkSelectionActions = () => {
    if (selectedItems.size === 0) return null;

    return (
      <div
        className="manage-items-bulk-actions"
        style={{ "--manage-items-theme": themeColor }}
      >
        <span className="manage-items-bulk-bar__count">
          {selectedItems.size} item{selectedItems.size > 1 ? "s" : ""} selected
        </span>
        <button
          type="button"
          className="manage-items-bulk-bar__btn manage-items-bulk-bar__btn--edit"
          onClick={handleBulkEdit}
          title="Edit Selected Items"
        >
          <i className="fa fa-edit" aria-hidden="true" />
          Edit
        </button>
        <button
          type="button"
          className="manage-items-bulk-bar__btn manage-items-bulk-bar__btn--delete"
          onClick={handleBulkDelete}
          title="Delete Selected Items"
        >
          <i className="fa fa-trash" aria-hidden="true" />
          Delete
        </button>
      </div>
    );
  };

  const handleDragStart = (e, item) => {
    if (e.target.closest("input[type='checkbox']")) {
      e.preventDefault();
      return;
    }

    const index = items.findIndex((i) => i.menuItem_ID === item.menuItem_ID);
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();

    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;

    if (
      draggedIndex === overIndex ||
      draggedIndex === undefined ||
      overIndex === undefined
    ) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    let newItems = [...items];
    const draggedItem = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(overIndex, 0, draggedItem);

    const sortOffset = (pageNumber - 1) * Number(pageSize.value);

    if (isCategorySortMode) {
      newItems = newItems.map((item, index) => ({
        ...item,
        categoryItem_SortOrder: index + 1 + sortOffset,
      }));

      if (newItems.some((item) => !item.relation_ID)) {
        showErrorToast("Cannot reorder items without a category relation.");
        fetchItemsData();
        dragItem.current = null;
        dragOverItem.current = null;
        return;
      }

      setItems(newItems);

      try {
        await axiosInstance.post(APIs.POST_UPDATE_ROOT_CATEGORY_ITEM_SORT_ORDER, {
          CompID,
          RootCat_ID: activeRootCatId,
          Updates: newItems.map((item) => ({
            ID: item.relation_ID,
            SortOrder: item.categoryItem_SortOrder,
          })),
        });
        showSuccessToast("Category item order updated.");
        fetchItemsData();
      } catch (error) {
        showErrorToast("Failed to update category item order.");
        fetchItemsData();
      }
    } else {
      newItems = newItems.map((item, index) => ({
        ...item,
        menuItem_Orderby: index + 1 + sortOffset,
      }));

      setItems(newItems);

      try {
        await axiosInstance.post(APIs.POST_UPDATE_ITEM_SORT_ORDER, {
          CompID: CompID,
          Updates: newItems.map((item) => ({
            MenuItem_ID: item.menuItem_ID,
            MenuItem_Orderby: item.menuItem_Orderby,
          })),
        });
        showSuccessToast("Item order updated.");
        fetchItemsData();
      } catch (error) {
        showErrorToast("Failed to update item order.");
        fetchItemsData();
      }
    }

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleStatusBadgeClick = (item) => {
    setStatusConfirmItem(item);
  };

  const closeStatusConfirmModal = () => {
    setStatusConfirmItem(null);
  };

  const confirmStatusChange = async () => {
    if (!statusConfirmItem) return;

    const { menuItem_ID, menuItem_Publish, menuItem_Name } = statusConfirmItem;
    const newStatus = !menuItem_Publish;

    try {
      const updatedItems = items.map((item) =>
        item.menuItem_ID === menuItem_ID
          ? { ...item, menuItem_Publish: newStatus }
          : item
      );
      setItems(updatedItems);

      await axiosInstance.post(APIs.POST_UPDATE_ITEM_STATUS, {
        CompID: CompID,
        MenuItem_ID: menuItem_ID,
        MenuItem_Publish: newStatus,
      });

      showSuccessToast(
        `"${menuItem_Name}" is now ${newStatus ? "Active" : "InActive"}.`
      );
      closeStatusConfirmModal();
    } catch (error) {
      const revertedItems = items.map((item) =>
        item.menuItem_ID === menuItem_ID
          ? { ...item, menuItem_Publish: menuItem_Publish }
          : item
      );
      setItems(revertedItems);
      showErrorToast("Failed to update item status.");
    }
  };

  const hasFiltersApplied = () => {
    return (
      search.trim() !== "" ||
      (status && status.value !== "") ||
      (category && category.value !== "")
    );
  };

  const isUnfilteredView = !hasFiltersApplied();

  const showEmptyCatalogState =
    initialItemsLoadComplete &&
    !loading &&
    isUnfilteredView &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialItemsLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    const parts = [];
    if (search.trim()) {
      parts.push(`search "${search.trim()}"`);
    }
    if (status?.value !== "" && status?.value !== undefined) {
      parts.push(`status "${status.label}"`);
    }
    if (category?.value) {
      parts.push(`category "${categorySelectValue?.label ?? category.label}"`);
    }
    if (parts.length === 0) {
      return "There are no items to show right now. Add a new item or check back later.";
    }
    return `No items match your current ${parts.join(", ")}. Try different filters or clear them to see all items.`;
  };

  const renderNoDataMessage = () => {
    if (loading || items.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No items found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Items</li>
          </ol>
          <h1 className="page-header mb-0">Manage Items</h1>
        </div>
        <div className="ms-auto">
          <div className="d-flex justify-content-end align-items-center mb-3">
            <button
              className="btn px-4"
              onClick={handleAddItemClick}
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: 'none',
                color: 'white',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: `0 2px 8px ${themeColor}40`
              }}
              onMouseEnter={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }}
            >
              <i className="fas fa-plus fa-lg me-2 ms-n2 text-white"></i>
              Add Item
            </button>
          </div>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {/* Show centered Add Item button only when no items exist and no filters are applied */}
        {!initialItemsLoadComplete && (
          <GridSectionLoader loading minHeight="400px" />
        )}

        {showEmptyCatalogState && (
          <AddButton onClick={handleAddItemClick} title="Add Item" />
        )}

        {showGridToolbar && (
          <>
            <ModernGridToolbar
              isDarkMode={isDarkMode}
              title="Items"
              totalCount={totalRecords}
              searchValue={search}
              onSearchChange={setSearch}
              searchPlaceholder="Search by name, category..."
              bodyFlush
              topFilters={
                <>
                  <ModernGridToolbarFilterPill
                    iconClass="fa fa-filter"
                    className="modern-grid-toolbar__filter-pill--status"
                  >
                    <Select
                      value={statusSelectValue}
                      onChange={(val) =>
                        dispatch(
                          setFilterField({
                            pageKey,
                            field: "status",
                            value: val,
                          })
                        )
                      }
                      options={statusOptions}
                      styles={filterPillSelectStyles}
                      placeholder="All Status"
                      isSearchable={false}
                      {...defaultSelectPortalProps}
                    />
                  </ModernGridToolbarFilterPill>
                  <ModernGridToolbarFilterPill iconClass="fa fa-folder" wide>
                    <Select
                      value={categorySelectValue}
                      onChange={(val) =>
                        dispatch(
                          setFilterField({
                            pageKey,
                            field: "category",
                            value: val,
                          })
                        )
                      }
                      options={categoryOptions}
                      styles={categoryFilterSelectStyles}
                      formatOptionLabel={formatCategoryOptionLabel}
                      placeholder="All Categories"
                      {...defaultSelectPortalProps}
                    />
                  </ModernGridToolbarFilterPill>
                  {renderBulkSelectionActions()}
                </>
              }
              headerControls={
                <ColumnSelection
                  visibleColumns={visibleColumns}
                  setVisibleColumns={(cols) =>
                    dispatch(setVisibleColumns({ pageKey, columns: cols }))
                  }
                  columnOrder={ITEM_GRID_COLUMN_ORDER}
                />
              }
            >
                  <div className="px-3 pb-3">
                  <GridSectionLoader loading={loading} minHeight="280px">
                  {renderNoDataMessage()}

                  {Array.isArray(items) && items.length > 0 && (
                    <>
                      <div
                        className="table-responsive table-container"
                        style={{
                          minHeight: "200px",
                          maxHeight: "450px",
                          overflowY: "auto",
                          overflowX: "auto",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        <table
                          className="table table-hover"
                          style={{
                            position: "relative",
                            "--modern-grid-sort-active": themeColor,
                          }}
                        >
                          <thead
                            className="bg-light sticky-header"
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 10000,
                              backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                              color: isDarkMode ? "#ffffff" : "#000000",
                            }}
                          >
                            <tr>
                              <th className="pt-2 pb-2" style={{ width: "72px" }}>
                                <input
                                  type="checkbox"
                                  onChange={handleSelectAll}
                                  checked={
                                    items.length > 0 &&
                                    selectedItems.size === items.length
                                  }
                                />
                              </th>
                              {visibleColumns.image && (
                                <th className="pt-2 pb-2 text-center" style={{ width: "56px" }}>
                                  Image
                                </th>
                              )}
                              {visibleColumns.name && (
                                <SortableColumnHeader
                                  sortField={ITEM_SORT_FIELDS.name}
                                  sortBy={sortBy}
                                  sortOrder={sortOrder}
                                  onSort={handleColumnSort}
                                >
                                  Item Name
                                </SortableColumnHeader>
                              )}
                              {visibleColumns.costPrice && (
                                <SortableColumnHeader
                                  sortField={ITEM_SORT_FIELDS.costPrice}
                                  sortBy={sortBy}
                                  sortOrder={sortOrder}
                                  onSort={handleColumnSort}
                                  className="text-center"
                                >
                                  Cost Price ({getCurrencySymbol(compCurrency)})
                                </SortableColumnHeader>
                              )}
                              {visibleColumns.SalePrice && (
                                <SortableColumnHeader
                                  sortField={ITEM_SORT_FIELDS.SalePrice}
                                  sortBy={sortBy}
                                  sortOrder={sortOrder}
                                  onSort={handleColumnSort}
                                  className="text-center"
                                >
                                  Sale Price ({getCurrencySymbol(compCurrency)})
                                </SortableColumnHeader>
                              )}
                              {visibleColumns.deliveryPrice && (
                                <SortableColumnHeader
                                  sortField={ITEM_SORT_FIELDS.deliveryPrice}
                                  sortBy={sortBy}
                                  sortOrder={sortOrder}
                                  onSort={handleColumnSort}
                                  className="text-center"
                                >
                                  Delivery Price ({getCurrencySymbol(compCurrency)})
                                </SortableColumnHeader>
                              )}
                              {visibleColumns.takeAwayPrice && (
                                <SortableColumnHeader
                                  sortField={ITEM_SORT_FIELDS.takeAwayPrice}
                                  sortBy={sortBy}
                                  sortOrder={sortOrder}
                                  onSort={handleColumnSort}
                                  className="text-center"
                                >
                                  TakeAway Price ({getCurrencySymbol(compCurrency)})
                                </SortableColumnHeader>
                              )}
                              {visibleColumns.status && (
                                <SortableColumnHeader
                                  sortField={ITEM_SORT_FIELDS.status}
                                  sortBy={sortBy}
                                  sortOrder={sortOrder}
                                  onSort={handleColumnSort}
                                  className="text-center"
                                >
                                  Status
                                </SortableColumnHeader>
                              )}
                              <th className="pt-2 pb-2 text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, index) => (
                              <tr
                                key={item.menuItem_ID}
                                draggable={canDragReorder}
                                onDragStart={(e) => canDragReorder && handleDragStart(e, item)}
                                onDragEnter={(e) => canDragReorder && handleDragEnter(e, index)}
                                onDragLeave={handleDragLeave}
                                onDragOver={(e) => canDragReorder && handleDragOver(e)}
                                onDrop={(e) => canDragReorder && handleDrop(e)}
                                onDragEnd={handleDragEnd}
                              >
                                <td
                                  className="align-middle"
                                  style={{ width: "72px" }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="d-flex align-items-center manage-items-grid__row-controls">
                                    <input
                                      type="checkbox"
                                      style={{ marginRight: "8px" }}
                                      checked={selectedItems.has(item.menuItem_ID)}
                                      onChange={(e) =>
                                        handleSelectItem(item.menuItem_ID, index, e)
                                      }
                                      data-chkbox-shiftsel="type1"
                                    />
                                    <i
                                      className="fa fa-bars text-secondary"
                                      style={{
                                        cursor: canDragReorder ? "grab" : "not-allowed",
                                        opacity: canDragReorder ? 1 : 0.35,
                                        marginRight: "10px",
                                      }}
                                      title={
                                        canDragReorder
                                          ? "Drag to reorder"
                                          : isCategorySortMode
                                            ? "Sort by Category Order to enable drag reorder"
                                            : "Sort by Order By to enable drag reorder"
                                      }
                                    />
                                  </div>
                                </td>
                                {visibleColumns.image && (
                                  <td
                                    className="align-middle text-center"
                                    onClick={() =>
                                      navigate(`/items/updateItem/${item.menuItem_ID}`)
                                    }
                                    style={{ cursor: "pointer", width: "56px" }}
                                  >
                                    <ItemGridThumbnail
                                      imagePath={item.menuItem_Image}
                                      alt={item.menuItem_Name}
                                      isDarkMode={isDarkMode}
                                    />
                                  </td>
                                )}
                                {visibleColumns.name && (
                                  <td
                                    className="align-middle"
                                    onClick={() =>
                                      navigate(
                                        `/items/updateItem/${item.menuItem_ID}`
                                      )
                                    }
                                    style={{ cursor: "pointer" }}
                                  >
                                    {item.menuItem_Name}
                                  </td>
                                )}
                                {visibleColumns.costPrice && (
                                  <td
                                    className="align-middle text-center"
                                    onClick={() =>
                                      navigate(
                                        `/items/updateItem/${item.menuItem_ID}`
                                      )
                                    }
                                    style={{ cursor: "pointer" }}
                                  >
                                    {item.menuItem_CostPrice?.toFixed(2) || "N/A"}
                                  </td>
                                )}
                                {visibleColumns.SalePrice && (
                                  <td
                                    className="align-middle text-center"
                                    onClick={() =>
                                      navigate(
                                        `/items/updateItem/${item.menuItem_ID}`
                                      )
                                    }
                                    style={{ cursor: "pointer" }}
                                  >
                                    {item.menuItem_SalePrice?.toFixed(2) || "N/A"}
                                  </td>
                                )}
                                {visibleColumns.deliveryPrice && (
                                  <td
                                    className="align-middle text-center"
                                    onClick={() =>
                                      navigate(
                                        `/items/updateItem/${item.menuItem_ID}`
                                      )
                                    }
                                    style={{ cursor: "pointer" }}
                                  >
                                    {item.menuItem_DeliveryPrice?.toFixed(2) || "N/A"}
                                  </td>
                                )}
                                {visibleColumns.takeAwayPrice && (
                                  <td
                                    className="align-middle text-center"
                                    onClick={() =>
                                      navigate(
                                        `/items/updateItem/${item.menuItem_ID}`
                                      )
                                    }
                                    style={{ cursor: "pointer" }}
                                  >
                                    {item.menuItem_TakeAwayPrice?.toFixed(2) || "N/A"}
                                  </td>
                                )}
                                {visibleColumns.status && (
                                  <td className="align-middle text-center">
                                    <StatusBadge
                                      isActive={item.menuItem_Publish}
                                      onClick={() => handleStatusBadgeClick(item)}
                                    />
                                  </td>
                                )}
                                <td className="align-middle text-center">
                                  <TableActionsMenu>
                                    <TableActionsItem
                                      as={Link}
                                      to={`/items/updateItem/${item.menuItem_ID}`}
                                      icon="fa fa-edit"
                                    >
                                      Edit
                                    </TableActionsItem>
                                    <TableActionsItem
                                      danger
                                      icon="fa fa-trash"
                                      onClick={() => handleDeleteItem(item)}
                                    >
                                      Delete
                                    </TableActionsItem>
                                  </TableActionsMenu>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
                  </GridSectionLoader>
                  </div>
            </ModernGridToolbar>
          </>
        )}
      </div>

      <CustomModal
        title="What type of item do you want to create"
        fontSize="18px"
        fontWeight={400}
        onClose={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
        isCentered={true}
        dialogMaxWidth="fit-content"
      >
        <ChoiceButtonGrid
          activeButton={selectedProductType}
          handleClick={handleProductTypeClick}
        />
      </CustomModal>

      <CustomModal
        title="What type of item do you want to create?"
        fontSize="18px"
        fontWeight={400}
        onClose={() => setIsSecondModalOpen(false)}
        isOpen={isSecondModalOpen}
        isCentered={true}
        dialogMaxWidth="fit-content"
      >
        <ChoiceButtonGrid
          buttons={secondButtons}
          activeButton={selectedSecondOption}
          handleClick={handleSecondClick}
        />
      </CustomModal>

      <CustomModal
        title="Change item status?"
        subtitle={
          statusConfirmItem
            ? `Are you sure you want to set "${statusConfirmItem.menuItem_Name}" to ${
                statusConfirmItem.menuItem_Publish ? "InActive" : "Active"
              }?`
            : ""
        }
        fontSize="18px"
        fontWeight={400}
        onClose={closeStatusConfirmModal}
        isOpen={!!statusConfirmItem}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText="Confirm"
        onSubmit={confirmStatusChange}
        dialogMaxWidth="400px"
        minHeight="auto"
        maxHeight="320px"
      />
    </div>
  );
};

export default ManageItems;

